import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import MaintenanceFormModal from "./MaintenanceFormModal";

const templateLabel = (t) => {
    if (t.serviceFor === "common") return "Common (all variants)";
    if (t.serviceFor === "transmissionType") return `Transmission: ${t.forValue}`;
    return `Fuel: ${t.forValue}`;
};

// A model's maintenance schedule lives as 1+ Maintenance documents, one per
// (serviceFor, forValue) combination - maintenanceModelList returns all of
// them for this modelId. Each is rendered as its own group with its
// serviceDetails as removable rows; "+ Add service" always goes through
// MaintenanceFormModal, which itself decides (server-side) whether that
// creates a new template or appends to an existing one.
const MaintenancePanel = ({ modelId, brandType }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: `/staff/maintenance/model/${modelId}` });
        setTemplates(response?.success ? response?.data?.data || [] : []);
        setLoading(false);
    }, [modelId]);

    useEffect(() => { load(); }, [load]);

    const removeService = async (maintenanceId, serviceId) => {
        setRemovingId(serviceId);
        const response = await Handler({ method: "patch", url: `/staff/maintenance/${maintenanceId}/service/${serviceId}/remove` });
        if (response?.success) {
            toast.success(response?.data?.message || "Service removed");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to remove service"));
        }
        setRemovingId(null);
    };

    if (loading) return <p className="text-muted mb-0">Loading maintenance schedule...</p>;

    return (
        <div>
            {templates.length ? (
                <div className="d-flex flex-column gap-3 mb-3">
                    {templates.map((t) => (
                        <div key={t.maintenanceId} className="border rounded-3 p-3">
                            <div className="fw-semibold small mb-2">{templateLabel(t)}</div>
                            {t.serviceDetails?.length ? (
                                <div className="d-flex flex-column gap-2">
                                    {t.serviceDetails.map((s) => (
                                        <div key={s.serviceId} className="d-flex align-items-center justify-content-between">
                                            <span className="small">{s.serviceName} <span className="text-muted">- every {s.spanTime}mo / {s.spanKm}km</span></span>
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeService(t.maintenanceId, s.serviceId)} disabled={removingId === s.serviceId}>
                                                {removingId === s.serviceId ? "..." : "Remove"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-muted small mb-0">No services in this template.</p>}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-muted small">No maintenance schedule defined for this model yet.</p>
            )}
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowAddModal(true)}>+ Add service</button>

            {showAddModal ? (
                <MaintenanceFormModal
                    modelId={modelId}
                    brandType={brandType}
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : null}
        </div>
    );
};

export default MaintenancePanel;
